/**
 * DebugPanel — Fixed expandable panel for step-through debugging.
 *
 * Shows the currently highlighted step, input/output data, error state.
 * Provides Previous Step, Next Step, Run to End, Stop controls.
 * Wired to useAppStore step-through methods.
 */

import {
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  Square,
} from 'lucide-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';

import { DebugStepInfo } from './DebugStepInfo';

export interface DebugPanelProps {
  open: boolean;
  onClose: () => void;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ open, onClose }) => {
  const { t } = useTranslation('editor');
  const { startStepThrough, stepThroughNext, stopStepThrough, runPipeline } = useAppStore();
  const pipeline = useEditorStore((s) => s.pipeline);
  const stepThrough = useExecutionStore((s) => s.stepThrough);
  const stepResults = useExecutionStore((s) => s.stepResults);
  const isProcessing = useExecutionStore((s) => s.isProcessing);
  const rerunFromStepIndex = useExecutionStore((s) => s.rerunFromStepIndex);

  const isActive = stepThrough?.active ?? false;
  const cursor = stepThrough?.cursor ?? 0;
  const currentStep = pipeline[cursor] ?? null;
  const currentStepName = currentStep?.label || currentStep?.toolId || 'Unknown';

  const currentResult = currentStep
    ? stepResults.find((r) => r.stepId === currentStep.id) ?? null
    : null;

  const handleStart = () => {
    if (!isActive) startStepThrough();
  };

  const handleNext = async () => {
    if (isActive && !isProcessing) {
      try { await stepThroughNext(); } catch (e) { console.error('Step-through error:', e); }
    }
  };

  const prevRunningRef = useRef(false);

  const handlePrev = () => {
    if (!isActive || cursor === 0 || prevRunningRef.current) return;
    prevRunningRef.current = true;
    stopStepThrough();
    startStepThrough();
    void (async () => {
      try {
        for (let i = 0; i < cursor - 1; i++) {
          await stepThroughNext();
        }
      } catch (e) {
        console.error('Step-through replay error:', e);
      } finally {
        prevRunningRef.current = false;
      }
    })();
  };

  const handleRunToEnd = async () => {
    try {
      if (isActive) {
        stopStepThrough();
      }
      await runPipeline();
      onClose();
    } catch (e) {
      console.error('Run-to-end error:', e);
    }
  };

  const handleStop = () => {
    stopStepThrough();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="border-b border-border bg-background shrink-0 overflow-hidden transition-all duration-200">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/30">
        <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">
          {t('debug.title')}
        </span>

        {isActive && (
          <span className="text-[10px] text-violet-400 font-medium">
            {t('debug.stepProgress', { current: cursor + 1, total: pipeline.length })}
          </span>
        )}

        {rerunFromStepIndex != null && (
          <span className="text-[10px] text-amber-400 font-medium">
            {t('debug.reranFromStep', { step: rerunFromStepIndex + 1 })}
          </span>
        )}

        <div className="flex-1" />

        {/* Controls */}
        <div className="flex items-center gap-1">
          {isActive && (
            <>
              <button
                className="h-5 px-1.5 text-[10px] rounded-[7px] border border-border text-muted-foreground hover:text-foreground hover:border-zinc-500 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('debug.previousStep')}
                aria-label={t('debug.previousStep')}
                onClick={handlePrev}
                disabled={cursor === 0 || isProcessing}
              >
                <ChevronLeft className="h-3 w-3" />
              </button>
              <button
                className={cn(
                  'h-5 px-1.5 text-[10px] rounded-[7px] border',
                  'border-violet-400/50 text-violet-400 hover:bg-violet-400/10',
                  'transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed'
                )}
                title={t('debug.nextStep')}
                aria-label={t('debug.nextStep')}
                onClick={handleNext}
                disabled={cursor >= pipeline.length || isProcessing || !!stepThrough?.failedStepId}
              >
                <ChevronRight className="h-3 w-3" />
              </button>
              <button
                className="h-5 px-1.5 text-[10px] rounded-[7px] border border-border text-muted-foreground hover:text-foreground hover:border-zinc-500 transition-all duration-150 flex items-center gap-1"
                title={t('debug.runToEnd')}
                aria-label={t('debug.runToEnd')}
                onClick={handleRunToEnd}
                disabled={isProcessing}
              >
                <ChevronsRight className="h-3 w-3" />
              </button>
              <button
                className="h-5 px-1.5 text-[10px] rounded-[7px] border border-rose-500/50 text-rose-500 hover:bg-rose-500/10 transition-all duration-150"
                title={t('debug.stop')}
                aria-label={t('debug.stopDebugging')}
                onClick={handleStop}
              >
                <Square className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-3 py-2 max-h-[200px] overflow-auto">
        {!isActive && pipeline.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-2">
            {t('debug.emptyPipeline')}
          </div>
        ) : !isActive ? (
          <div className="text-xs text-muted-foreground italic py-2">
            {t('debug.pressStart')}
          </div>
        ) : cursor >= pipeline.length ? (
          <div className="text-xs text-emerald-500 py-2">
            {t('debug.allCompleted')}
          </div>
        ) : (
          <DebugStepInfo
            stepName={currentStepName}
            stepIndex={cursor}
            result={currentResult}
          />
        )}
      </div>

      {/* Start Debug bar */}
      {!isActive && (
        <div className="px-3 pb-2">
          <button
            className={cn(
              'w-full h-9 text-xs font-medium rounded-lg',
              'bg-violet-600 hover:bg-violet-700 text-white',
              'transition-all duration-150 flex items-center justify-center gap-1.5',
              pipeline.length === 0 && 'opacity-40 cursor-not-allowed'
            )}
            title={t('debug.startStepThrough')}
            onClick={handleStart}
            disabled={pipeline.length === 0}
          >
            ▶ {t('debug.startDebug')}
          </button>
        </div>
      )}
    </div>
  );
};
